import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import sliderService, { CreateSliderRequest, UpdateSliderRequest } from '../../../services/slider';
import { ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const SliderFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<Partial<CreateSliderRequest>>({
    title: '',
    subtitle: '',
    image_url: '',
    link_url: '',
    link_type: 'internal', // internal or external
    link_target: '_self', // _self or _blank
    display_order: 0,
    is_active: true,
  });

  useEffect(() => {
    if (id) {
      loadSlider(id);
    }
  }, [id]);

  const loadSlider = async (sliderId: string) => {
    try {
      const slider = await sliderService.getById(sliderId);
      setFormData({
        title: slider.title,
        subtitle: slider.subtitle || '',
        image_url: slider.image_url,
        link_url: slider.link_url || '',
        link_type: slider.link_type || 'internal',
        link_target: slider.link_target || '_self',
        display_order: slider.display_order,
        is_active: slider.is_active,
        start_date: slider.start_date,
        end_date: slider.end_date,
      });
      setPreviewUrl(slider.image_url);
    } catch (error) {
      console.error('Error loading slider:', error);
      toast.error('خطا در بارگذاری اطلاعات اسلایدر');
      navigate('/admin/sliders');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title) {
        toast.error('عنوان الزامی است');
        return;
    }

    setLoading(true);
    try {
      let imageUrl = formData.image_url;

      // Upload image if selected
      if (file) {
        imageUrl = await sliderService.uploadImage(file);
      }

      if (!imageUrl) {
          toast.error('تصویر الزامی است');
          setLoading(false);
          return;
      }

      const dataToSave = {
        ...formData,
        image_url: imageUrl,
      } as CreateSliderRequest;

      if (isEditMode && id) {
        await sliderService.update(id, dataToSave as UpdateSliderRequest);
        toast.success('اسلایدر با موفقیت ویرایش شد');
      } else {
        await sliderService.create(dataToSave);
        toast.success('اسلایدر جدید با موفقیت ایجاد شد');
      }

      navigate('/admin/sliders');
    } catch (error: any) {
      console.error('Error saving slider:', error);
      const message = error.response?.data?.error || 'خطا در ذخیره اسلایدر';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
      return <div className="p-8 text-center">در حال بارگذاری...</div>;
  }

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <button 
            onClick={() => navigate('/admin/sliders')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
            <ArrowRight className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'ویرایش اسلایدر' : 'افزودن اسلایدر جدید'}
        </h1>
      </div>

      <div className="card p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload */}
            <div className="flex justify-center">
                <div 
                    className="relative w-full h-64 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl overflow-hidden flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors group"
                    onClick={() => fileInputRef.current?.click()}
                >
                    {previewUrl ? (
                        <>
                            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <span className="text-white font-medium">تغییر تصویر</span>
                            </div>
                        </>
                    ) : (
                        <div className="text-center text-gray-500">
                            <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-sm">برای انتخاب تصویر کلیک کنید</span>
                        </div>
                    )}
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileChange} 
                        className="hidden" 
                        accept="image/*"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">عنوان</label>
                <input
                type="text"
                className="input-field w-full"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                />
            </div>

            {/* Subtitle */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">زیرعنوان (اختیاری)</label>
                <input
                type="text"
                className="input-field w-full"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                />
            </div>

            {/* Link URL */}
            <div className="md:col-span-2 space-y-3">
                <label className="block text-sm font-medium text-gray-700">تنظیمات لینک (اختیاری)</label>
                <div className="flex flex-col md:flex-row gap-4">
                    <select 
                        className="input-field w-full md:w-40"
                        value={formData.link_type}
                        onChange={(e) => setFormData({ ...formData, link_type: e.target.value })}
                    >
                        <option value="internal">لینک داخلی</option>
                        <option value="external">لینک خارجی</option>
                    </select>

                    <input
                        type="text"
                        className="input-field flex-1"
                        value={formData.link_url}
                        onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                        placeholder={formData.link_type === 'internal' ? 'مثال: /products' : 'مثال: https://google.com'}
                        dir="ltr"
                    />

                    <select 
                        className="input-field w-full md:w-32"
                        value={formData.link_target}
                        onChange={(e) => setFormData({ ...formData, link_target: e.target.value })}
                    >
                        <option value="_self">همین صفحه</option>
                        <option value="_blank">تب جدید</option>
                    </select>
                </div>
                {formData.link_type === 'internal' && (
                    <p className="text-xs text-gray-500">
                        برای لینک به محصول خاص، می‌توانید از فرمت <code className="bg-gray-100 px-1 rounded">/products/sku/CODE</code> استفاده کنید.
                    </p>
                )}
            </div>

            {/* Order & Active */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ترتیب نمایش</label>
                <input
                type="number"
                className="input-field w-full"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
            </div>

            <div className="flex items-end">
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl w-full cursor-pointer hover:bg-gray-50 h-[46px]">
                <input
                    type="checkbox"
                    className="w-5 h-5 text-[#0e444e] rounded focus:ring-[#0e444e]"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <span className="text-sm font-medium text-gray-700">فعال</span>
                </label>
            </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-100">
                <button
                    type="button"
                    onClick={() => navigate('/admin/sliders')}
                    className="px-6 py-2.5 rounded-xl text-gray-600 hover:bg-gray-100 ml-2"
                >
                    انصراف
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2.5 rounded-xl bg-[#0e444e] text-white hover:bg-[#0b363e] shadow-lg shadow-[#0e444e]/20 font-medium transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    {loading ? (
                    <>
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        در حال ذخیره...
                    </>
                    ) : (
                    'ذخیره تغییرات'
                    )}
                </button>
            </div>
        </form>
      </div>
    </div>
  );
};

export default SliderFormPage;
